import { ref } from 'vue'
import { defineStore, type StateTree } from 'pinia'
import { useToast } from 'vue-toastification'
import * as ApiService from '@/core/services/ApiService'
import taskRequest from '@/api/task-request'
import type { CaseHistoryOat } from '@/types/api'
import { limitDistinctOats, outcomeAssessmentLimitFor } from '@/types/task-actions'
import {
  useProfileStore,
  getFacilityKey,
  getFacilityKeyForPortal,
  getPatientKey
} from '@/stores/profile'
import { useTaskStore } from '@/stores/task'
import { logError } from '@/utils/safeErrorLog'

/** Map GetCaseHistoryOats (PatientPortal) response item to ProblemOat (prompt, questions with description → question, answer, answers). */
function mapCaseHistoryOatToProblemOat(cho: CaseHistoryOat): ProblemOat {
  return {
    oatID: cho.oatID,
    prompt: cho.prompt ?? '',
    questions: (cho.questions ?? []).map((q, index) => ({
      ordinal: q.oatQuestionID ?? index,
      question: q.description ?? '',
      answer: q.answer ?? '',
      answers: Array.isArray(q.answers) ? q.answers : []
    }))
  }
}

type SelectedBodyPart = {
  partName: string
  side: string
  label: string
}

interface ComplaintValues {
  code: string
  value: string
}

export interface ComplaintHeader {
  id?: string | number
  complaintType: string
  region: string
  description?: string
  complete?: boolean
}

/** Portal API GetCaseHistoryProblems response item (swagger CaseHistoryProblem) */
interface CaseHistoryProblem {
  id: number
  ordinal?: number
  complaintType?: string | null
  region?: string | null
  name?: string | null
  oatID?: number
  oatDescription?: string | null
  complete?: boolean
}

interface PrimaryComplaintDemographics {
  firstName: string
  mi: string
  lastName: string
  nickname: string
  dob: string
  gender: string
  maritalStatID: number
  address: string
  address2: string
  city: string
  state: string
  zip: string
  phoneType: string
  phone: string
  email: string
  contactMethod: string
  emerContact: string
  emerRelation: string
  emerPhone: string
}

interface HumanBodyState {
  selectedPart?: SelectedBodyPart | null
  selectedInternalPart?: string | null
}

export enum VisitReason {
  PAIN = 'pain',
  NUMBNESS = 'numbness',
  WEAKNESS = 'weakness',
  HEADACHES = 'headaches',
  DIZZINESS = 'dizziness',
  WELLNESS = 'wellness'
}

export enum ComplaintHistoryStep {
  ComplaintHistory1 = 'ComplaintHistory1',
  ComplaintHistory2 = 'ComplaintHistory2',
  ComplaintHistory3 = 'ComplaintHistory3'
}

export enum QualityOptions {
  sharp = 'sharp',
  dull = 'dull',
  aching = 'aching',
  throbbing = 'throbbing',
  burning = 'burning',
  stabbing = 'stabbing',
  shooting = 'shooting',
  tingling = 'tingling',
  Numbness = 'numbness',
  Other = 'other'
}

interface OatQuestion {
  ordinal: number
  question: string
  answer: string
  answers: string[]
}

export interface ProblemOat {
  oatID: number
  prompt: string
  questions: OatQuestion[]
}

const visitReasonLabels: Record<VisitReason, string> = {
  [VisitReason.PAIN]: 'Pain',
  [VisitReason.NUMBNESS]: 'Numbness',
  [VisitReason.WEAKNESS]: 'Weakness',
  [VisitReason.HEADACHES]: 'Headaches',
  [VisitReason.DIZZINESS]: 'Dizziness',
  [VisitReason.WELLNESS]: 'Wellness'
}

function getPatientId(): string {
  const p = useProfileStore().currentPatient
  return p?.patientID?.toString() ?? p?.id?.toString() ?? ''
}

/** Portal routes expect facility GUID when available (see getFacilityKeyForPortal). */
function portalFacilityKey(): string {
  return getFacilityKeyForPortal() || getFacilityKey()
}

/**
 * `/Portal/.../{patientKey}` must use the opaque portal key when the API issued one.
 * Call sites often pass numeric `patientID`; if it matches the logged-in patient,
 * prefer link key → `currentPatient.patientKey` → numeric id — otherwise 403 Forbidden.
 */
function resolvePortalPatientKey(explicit?: string | number | null): string {
  const state = useProfileStore()
  const p = state.currentPatient
  const linkKey =
    state.linkInfo?.patientKey && String(state.linkInfo.patientKey).trim()
      ? String(state.linkInfo.patientKey).trim()
      : ''
  const opaque = p?.patientKey && String(p.patientKey).trim() ? String(p.patientKey).trim() : ''
  const pid = p?.patientID != null ? String(p.patientID) : ''

  if (explicit == null || String(explicit).trim() === '') {
    return linkKey || opaque || pid
  }
  const want = String(explicit).trim()
  if (pid && want === pid) {
    if (linkKey) return linkKey
    if (opaque) return opaque
    return pid
  }
  return want
}

/**
 * Resolve case history for this patient via the API. Do not use a stale
 * persisted `caseHistory.id` — it may belong to a previously selected patient.
 */
async function ensureCaseHistoryIdForPatient(
  facilityKey: string,
  patientKey: string
): Promise<number> {
  if (!facilityKey || !patientKey) return 0
  const findRes = await taskRequest.request.findIncompleteCaseHistory(facilityKey, patientKey)
  const foundId =
    typeof findRes?.data === 'number'
      ? findRes.data
      : (findRes?.data as { id?: number } | undefined)?.id
  if (foundId != null && foundId > 0) {
    useTaskStore().setCurrentCaseHistoryId(foundId)
    return foundId
  }
  const insertRes = await taskRequest.request.insertCaseHistory(facilityKey, patientKey, {
    aa: 'false'
  })
  const newId =
    typeof insertRes?.data === 'number'
      ? insertRes.data
      : (insertRes?.data as { id?: number } | undefined)?.id
  if (newId != null && newId > 0) {
    useTaskStore().setCurrentCaseHistoryId(newId)
    return newId
  }
  return 0
}

interface ComplaintHistoryState {
  step: ComplaintHistoryStep
  answers: {
    onset: string
    specificDate: string
    start: string
    startDetails: string
    severityRating: number
    frequency: string
    qualities: QualityOptions[]
  }
}

interface PrimaryComplaintConfig {
  gender: 'M' | 'F' | null
}

/**
 * Surface a save failure to the patient so nothing is ever lost silently. A stable toast id means
 * repeated failures on a flaky connection replace the message rather than stacking up.
 */
function notifySaveFailure() {
  useToast().error(
    "We couldn't save your last answer — check your connection and try again. Your entries are still here.",
    { id: 'complaint-save-error' }
  )
}

function defaultComplaintHistory(): ComplaintHistoryState {
  return {
    step: ComplaintHistoryStep.ComplaintHistory1,
    answers: {
      onset: '',
      specificDate: '',
      start: '',
      startDetails: '',
      severityRating: 5,
      frequency: '',
      qualities: []
    }
  }
}

function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : 'An error occurred'
}

/**
 * What React's `partialize` writes to localStorage `primary-complaint`: the whole state, with the
 * complaint lists (patient-specific, from the API) and the transient fields reset.
 */
export function partializePrimaryComplaint(state: StateTree): StateTree {
  return {
    ...state,
    complaintHeaders: null,
    currentComplaintHeader: null,
    currentComplaintValues: null,
    problemOat: null,
    loading: false,
    error: null,
    // A save in flight when the tab closed never decrements; persisting it would trigger the
    // "leave site?" prompt forever. The body-map list belongs to one complaint's add form.
    pendingSaves: 0,
    bodyMap: null
  }
}

/** In-flight save per complaint field ("problemId:code"); the next save of that field waits for it. */
const saveChains = new Map<string, Promise<unknown>>()

/**
 * The complaint being added or edited, the patient's complaints, and the outcome assessment.
 * Persisted under localStorage `primary-complaint` (zustand format, version 0) through
 * `partializePrimaryComplaint`, as in React.
 */
export const usePrimaryComplaintStore = defineStore(
  'primary-complaint',
  () => {
    const loading = ref(false)
    const error = ref<string | null>(null)
    const bodyMap = ref<string[] | null>(null)
    const humanBody = ref<HumanBodyState | null>(null)
    const visitReason = ref<VisitReason | null>(null)
    const config = ref<PrimaryComplaintConfig>({ gender: null })
    const complaintHistory = ref<ComplaintHistoryState>(defaultComplaintHistory())
    const generalRegion = ref<string | null>(null)
    const demographics = ref<PrimaryComplaintDemographics | null>(null)
    const complaintHeaders = ref<ComplaintHeader[] | null>(null)
    const currentComplaintHeader = ref<ComplaintHeader | null>(null)
    const currentComplaintValues = ref<ComplaintValues[] | null>(null)
    const problemOat = ref<ProblemOat | null>(null)
    /** Number of complaint/outcome saves currently in flight (drives the leave-page guard). */
    const pendingSaves = ref(0)

    function setHumanBody(newHumanBody: Partial<HumanBodyState>) {
      humanBody.value = humanBody.value
        ? { ...humanBody.value, ...newHumanBody }
        : { selectedPart: undefined, selectedInternalPart: undefined, ...newHumanBody }
    }

    function clearHumanBody() {
      humanBody.value = null
    }

    function clearBodyMap() {
      bodyMap.value = null
    }

    function setVisitReason(reason: VisitReason | null) {
      visitReason.value = reason
    }

    function resetVisitReason() {
      visitReason.value = null
    }

    function setProblemOat(oat: ProblemOat) {
      problemOat.value = oat
    }

    function setComplaintHistoryStep(step: ComplaintHistoryStep) {
      complaintHistory.value = { ...complaintHistory.value, step }
    }

    function startNewComplaint() {
      visitReason.value = null
      humanBody.value = null
      complaintHistory.value = defaultComplaintHistory()
      complaintHeaders.value = null
      currentComplaintHeader.value = null
      currentComplaintValues.value = null
      problemOat.value = null
    }

    async function fetchBodyMap(partName: string, side: string): Promise<void> {
      loading.value = true
      error.value = null
      bodyMap.value = null
      const locale = 'en'
      try {
        const response = await ApiService.get(
          `/Portal/GetRegions/${portalFacilityKey()}/${side}/${partName}/${locale}`
        )
        bodyMap.value = response.data as string[]
      } catch (err) {
        error.value = errorMessage(err)
      } finally {
        loading.value = false
      }
    }

    async function fetchComplaints(
      patientID: string | number = getPatientKey() || getPatientId()
    ): Promise<ComplaintHeader[] | null> {
      loading.value = true
      error.value = null
      complaintHeaders.value = null
      const facilityKey = portalFacilityKey()
      const patientKey = resolvePortalPatientKey(patientID)
      try {
        const caseHistoryId = await ensureCaseHistoryIdForPatient(facilityKey, patientKey)
        if (caseHistoryId === 0) {
          complaintHeaders.value = []
          return []
        }
        const response = await taskRequest.request.getCaseHistoryProblems(
          facilityKey,
          caseHistoryId
        )
        const problems = (response.data as CaseHistoryProblem[]) ?? []
        const headers: ComplaintHeader[] = problems.map((p) => ({
          id: p.id,
          complaintType: p.complaintType ?? '',
          region: p.region ?? '',
          description: p.name ?? undefined,
          complete: p.complete
        }))
        complaintHeaders.value = headers
        return headers
      } catch (err) {
        error.value = errorMessage(err)
        logError('API Error:', err)
        throw err
      } finally {
        loading.value = false
      }
    }

    async function createComplaint(
      id: string | number = getPatientKey() || getPatientId(),
      description = ''
    ): Promise<unknown> {
      loading.value = true
      error.value = null
      const facilityKey = portalFacilityKey()
      const patientKey = resolvePortalPatientKey(id)
      try {
        const caseHistoryId = await ensureCaseHistoryIdForPatient(facilityKey, patientKey)
        if (caseHistoryId === 0) {
          throw new Error('Could not find or create case history')
        }
        const problemsRes = await taskRequest.request.getCaseHistoryProblems(
          facilityKey,
          caseHistoryId
        )
        const problems = (problemsRes.data as CaseHistoryProblem[]) ?? []
        const ordinal = problems.length + 1
        const complaintType = visitReason.value ?? ''
        const regionFromBody = humanBody.value?.selectedPart?.label ?? ''
        const nameFromList = humanBody.value?.selectedInternalPart ?? ''
        const region = regionFromBody || nameFromList
        const complaintTypeLabel = complaintType
          ? complaintType.charAt(0).toUpperCase() + complaintType.slice(1)
          : ''
        const name =
          complaintTypeLabel && nameFromList
            ? `${complaintTypeLabel} in the ${nameFromList}`
            : nameFromList || description
        const response = await taskRequest.request.insertCaseHistoryProblem(
          facilityKey,
          caseHistoryId,
          ordinal,
          {
            complaintType,
            region,
            name: name || ''
          }
        )
        const raw = response.data as { id?: number } | number | undefined
        const problemId = typeof raw === 'number' ? raw : raw?.id
        currentComplaintHeader.value = {
          id: problemId,
          complaintType,
          region,
          description: name || undefined
        }
        return problemId
      } catch (err) {
        error.value = errorMessage(err)
        logError('API Error:', err)
        throw err
      } finally {
        loading.value = false
      }
    }

    async function updateComplaint(
      problemId: string | number,
      explicitPatientKey: string | number = getPatientKey() || getPatientId()
    ): Promise<void> {
      const id = Number(problemId)
      if (Number.isNaN(id)) return
      const facilityKey = portalFacilityKey()
      const patientKey = resolvePortalPatientKey(explicitPatientKey)
      const complaintType = visitReason.value ?? ''
      const regionFromBody = humanBody.value?.selectedPart?.label ?? ''
      const nameFromList = humanBody.value?.selectedInternalPart ?? ''
      const region = regionFromBody || nameFromList
      const complaintTypeLabel = complaintType
        ? complaintType.charAt(0).toUpperCase() + complaintType.slice(1)
        : ''
      const name =
        complaintTypeLabel && nameFromList
          ? `${complaintTypeLabel} in the ${nameFromList}`
          : nameFromList || ''
      loading.value = true
      error.value = null
      try {
        await taskRequest.request.updateCaseHistoryProblem(facilityKey, id, {
          complaintType,
          region,
          name: name || ''
        })
        const patient = String(patientKey ?? '')
        await fetchComplaints(patient)
        const headers = complaintHeaders.value ?? []
        const updated = headers.find((h) => Number(h.id) === id)
        if (updated) {
          currentComplaintHeader.value = {
            ...updated,
            complaintType,
            region,
            description: name || undefined
          }
        }
      } catch (err) {
        error.value = errorMessage(err)
        throw err
      } finally {
        loading.value = false
      }
    }

    async function fetchComplaintValues(
      // Kept for call-site compatibility; the id actually comes from currentComplaintHeader below.
      _patientID: string | number = getPatientId(),
      complaintId: string | number | undefined = currentComplaintHeader.value?.id
    ): Promise<unknown> {
      if (complaintId == null) return
      const problemId = Number(complaintId)
      if (Number.isNaN(problemId)) return
      loading.value = true
      error.value = null
      try {
        const response = await taskRequest.request.getCaseHistoryProblemValues(
          portalFacilityKey(),
          problemId
        )
        currentComplaintValues.value = (response.data as ComplaintValues[]) ?? null
        return response.data
      } catch (err) {
        error.value = errorMessage(err)
        logError('API Error:', err)
        throw err
      } finally {
        loading.value = false
      }
    }

    async function fetchComplaintValue(complaintId: string, code: string): Promise<unknown> {
      const problemId = Number(complaintId)
      if (Number.isNaN(problemId)) return
      loading.value = true
      error.value = null
      try {
        const response = await taskRequest.request.getCaseHistoryProblemValues(
          portalFacilityKey(),
          problemId
        )
        const list = (response.data as ComplaintValues[]) ?? []
        const item = list.find((v) => v.code === code)
        return item?.value
      } catch (err) {
        error.value = errorMessage(err)
        throw err
      } finally {
        loading.value = false
      }
    }

    async function updateComplaintValue(code: string, value: unknown): Promise<unknown> {
      const complaintId = currentComplaintHeader.value?.id
      if (complaintId == null) return
      const problemId = Number(complaintId)
      if (Number.isNaN(problemId)) return
      loading.value = true
      error.value = null
      pendingSaves.value += 1
      // Saves of the same field run one after another, so the patient's last tap is the last write.
      // Unordered requests could land out of order and leave an answer the patient took back.
      const key = `${problemId}:${code}`
      const previous = saveChains.get(key) ?? Promise.resolve()
      const run = previous
        .catch(() => {})
        .then(() =>
          taskRequest.request.updateCaseHistoryProblemValue(portalFacilityKey(), problemId, {
            code,
            value: value != null ? String(value) : undefined
          })
        )
      saveChains.set(key, run)
      try {
        const response = await run
        return response.data
      } catch (err) {
        error.value = errorMessage(err)
        notifySaveFailure()
        throw err
      } finally {
        if (saveChains.get(key) === run) saveChains.delete(key)
        loading.value = false
        pendingSaves.value = Math.max(0, pendingSaves.value - 1)
      }
    }

    async function fetchProblemOat(intakeProblemId: string | number): Promise<unknown> {
      const problemId = Number(intakeProblemId)
      if (Number.isNaN(problemId)) return null
      loading.value = true
      error.value = null
      problemOat.value = null
      try {
        const response = await taskRequest.request.getCaseHistoryProblemOat(
          portalFacilityKey(),
          problemId
        )
        const raw = response.data as CaseHistoryOat | null | undefined
        // Without the case history's full list the distinct-assessment order is unknown here,
        // so only "no outcome assessments" (limit 0) is enforced on this path.
        const oatLimit = outcomeAssessmentLimitFor(useTaskStore().portalConfigMeta)
        const data =
          raw && typeof raw === 'object' && raw.oatID != null && oatLimit !== 0
            ? mapCaseHistoryOatToProblemOat(raw)
            : null
        problemOat.value = data
        return data
      } catch (err) {
        error.value = errorMessage(err)
        throw err
      } finally {
        loading.value = false
      }
    }

    async function fetchCaseHistoryOats(
      facilityId: string | number,
      caseHistoryId: string | number,
      caseHistoryProblemID?: string | number
    ): Promise<unknown> {
      loading.value = true
      error.value = null
      problemOat.value = null
      try {
        const response = await taskRequest.request.getCaseHistoryOats(
          String(facilityId),
          Number(caseHistoryId)
        )
        const raw: unknown = response.data
        // Unwrap: API may return array or { oats: [] } / { data: [] }
        const fullList: CaseHistoryOat[] = Array.isArray(raw)
          ? raw
          : raw && typeof raw === 'object'
            ? ((raw as { oats?: CaseHistoryOat[] }).oats ??
              (raw as { data?: CaseHistoryOat[] }).data ??
              (raw ? [raw as CaseHistoryOat] : []))
            : []
        // The config caps how many distinct assessments the patient is asked across all their
        // complaints: keep the first N in the API's order; later complaints sharing one still get it.
        const list = limitDistinctOats(
          fullList,
          outcomeAssessmentLimitFor(useTaskStore().portalConfigMeta)
        )
        const problemId = caseHistoryProblemID != null ? Number(caseHistoryProblemID) : undefined
        // Only show the OAT for this complaint; never show all or another complaint's questions
        const cho =
          problemId != null ? list.find((o) => o.caseHistoryProblemID === problemId) : list[0]
        const data = cho ? mapCaseHistoryOatToProblemOat(cho) : null
        problemOat.value = data
        return data
      } catch (err) {
        error.value = errorMessage(err)
        throw err
      } finally {
        loading.value = false
      }
    }

    async function updateProblemOat(
      intakeProblemId: string | number,
      oat: ProblemOat,
      index: number
    ): Promise<unknown> {
      const problemId = Number(intakeProblemId)
      if (Number.isNaN(problemId)) return
      loading.value = true
      error.value = null
      pendingSaves.value += 1
      try {
        await taskRequest.request.updateCaseHistoryProblemOatAnswers(
          portalFacilityKey(),
          problemId,
          oat.oatID,
          [
            {
              oatID: oat.oatID,
              question: oat.questions[index]?.question,
              answer: oat.questions[index]?.answer
            }
          ]
        )
      } catch (err) {
        error.value = errorMessage(err)
        notifySaveFailure()
        throw err
      } finally {
        loading.value = false
        pendingSaves.value = Math.max(0, pendingSaves.value - 1)
      }
    }

    async function fetchRadiatingList(region: string): Promise<unknown> {
      loading.value = true
      error.value = null
      const normalize = (data: unknown): string[] => {
        if (typeof data === 'string') {
          try {
            return JSON.parse(data) as string[]
          } catch {
            return []
          }
        }
        return Array.isArray(data) ? data : []
      }
      try {
        const response = await ApiService.get(`/Portal/RadiatingList/${encodeURIComponent(region)}`)
        return normalize(response.data)
      } catch (err) {
        error.value = errorMessage(err)
        throw err
      } finally {
        loading.value = false
      }
    }

    async function completeComplaint(
      intakeProblemId: string | number,
      complete = true
    ): Promise<unknown> {
      const problemId = Number(intakeProblemId)
      if (Number.isNaN(problemId)) return
      loading.value = true
      error.value = null
      try {
        const response = await taskRequest.request.updateCaseHistoryProblemComplete(
          portalFacilityKey(),
          problemId,
          complete
        )
        return response.data
      } catch (err) {
        error.value = errorMessage(err)
        useToast().error(
          "We couldn't finish saving this complaint — check your connection and try again.",
          { id: 'complaint-complete-error' }
        )
        throw err
      } finally {
        loading.value = false
      }
    }

    async function deleteComplaint(
      problemId: string | number,
      patientKey: string | number = getPatientKey() || getPatientId()
    ): Promise<void> {
      const id = Number(problemId)
      if (Number.isNaN(id)) return
      const facilityKey = portalFacilityKey()
      const patient = String(patientKey ?? '')
      const caseHistoryId = await ensureCaseHistoryIdForPatient(facilityKey, patient)
      if (caseHistoryId === 0) {
        throw new Error('Could not find case history')
      }
      loading.value = true
      error.value = null
      try {
        await taskRequest.request.deleteCaseHistoryProblem(facilityKey, caseHistoryId, id)
        await fetchComplaints(patient)
      } catch (err) {
        error.value = errorMessage(err)
        throw err
      } finally {
        loading.value = false
      }
    }

    function clearData() {
      bodyMap.value = null
      error.value = null
    }

    return {
      loading,
      error,
      bodyMap,
      humanBody,
      visitReason,
      config,
      complaintHistory,
      generalRegion,
      demographics,
      complaintHeaders,
      currentComplaintHeader,
      currentComplaintValues,
      problemOat,
      pendingSaves,
      setHumanBody,
      clearHumanBody,
      clearBodyMap,
      setVisitReason,
      resetVisitReason,
      setProblemOat,
      setComplaintHistoryStep,
      startNewComplaint,
      fetchBodyMap,
      fetchComplaints,
      createComplaint,
      updateComplaint,
      fetchComplaintValues,
      fetchComplaintValue,
      updateComplaintValue,
      fetchProblemOat,
      fetchCaseHistoryOats,
      updateProblemOat,
      fetchRadiatingList,
      completeComplaint,
      deleteComplaint,
      clearData
    }
  },
  {
    zustandPersist: { key: 'primary-complaint', partialize: partializePrimaryComplaint }
  }
)

export { visitReasonLabels }
